import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Copy, ArrowRight, Coffee, MonitorSmartphone, Shield } from "lucide-react";
import { toast } from "sonner";

const SignupSuccess = () => {
    const { slug } = useParams();
    const nav = useNavigate();
    const { state } = useLocation();
    const cafeName = state?.cafeName || slug;
    const pin = state?.pin;

    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const customerUrl = `${origin}/${slug}`;
    const staffUrl = `${origin}/${slug}/staff`;
    const adminUrl = `${origin}/${slug}/admin`;

    const copy = (text, label) => {
        navigator.clipboard.writeText(text);
        toast.success(`${label} copied`);
    };

    return (
        <div className="min-h-screen bg-[#F9F6F0] text-[#2C1A12] px-6 py-12">
            <div className="max-w-2xl mx-auto">
                <motion.div
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 15 }}
                    className="mx-auto h-20 w-20 rounded-full bg-[#1B995E] flex items-center justify-center shadow-[0_16px_40px_rgba(27,153,94,0.32)]"
                >
                    <CheckCircle2 className="h-10 w-10 text-white" strokeWidth={2.5} />
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="mt-8 text-center"
                >
                    <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                        {cafeName} is live
                    </div>
                    <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl leading-tight">
                        You're live. ☕
                    </h1>
                    <p className="mt-3 text-[#8C7A72] max-w-lg mx-auto">
                        Save these 3 links. Print the customer QR. Bookmark the staff link on your
                        counter iPad. Keep the admin PIN safe.
                    </p>
                </motion.div>

                <div className="mt-10 space-y-4">
                    <LinkCard
                        icon={Coffee}
                        title="Customer ordering"
                        subtitle="Print this URL as a QR code for the counter"
                        url={customerUrl}
                        testid="success-customer-url"
                        onCopy={() => copy(customerUrl, "Customer link")}
                        accent="#FF4F18"
                    />
                    <LinkCard
                        icon={MonitorSmartphone}
                        title="Kitchen / staff queue"
                        subtitle="Bookmark this on your counter iPad · always on"
                        url={staffUrl}
                        testid="success-staff-url"
                        onCopy={() => copy(staffUrl, "Staff link")}
                        accent="#2C1A12"
                    />
                    <LinkCard
                        icon={Shield}
                        title="Owner dashboard"
                        subtitle={pin ? `Menu, orders, customers, reports · PIN: ${pin}` : "Menu, orders, customers, reports"}
                        url={adminUrl}
                        testid="success-admin-url"
                        onCopy={() => copy(adminUrl, "Admin link")}
                        accent="#1B995E"
                    />
                </div>

                <div className="mt-10 rounded-3xl bg-white border border-[#E8DCCB] p-6">
                    <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8C7A72]">
                        Next 5 minutes
                    </div>
                    <ol className="mt-3 space-y-2 text-sm">
                        <li className="flex gap-3">
                            <span className="font-display font-black text-[#FF4F18]">1.</span>
                            <span>
                                Open <span className="font-mono">{adminUrl}</span> → log in with your PIN →
                                add your menu items (or use the defaults).
                            </span>
                        </li>
                        <li className="flex gap-3">
                            <span className="font-display font-black text-[#FF4F18]">2.</span>
                            <span>
                                Generate a QR for the customer link using{" "}
                                <a
                                    href={`https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(customerUrl)}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="underline text-[#FF4F18] font-semibold"
                                >
                                    this free QR generator
                                </a>
                                . Print + laminate.
                            </span>
                        </li>
                        <li className="flex gap-3">
                            <span className="font-display font-black text-[#FF4F18]">3.</span>
                            <span>
                                Open <span className="font-mono">{staffUrl}</span> on the counter iPad.
                                Set Auto-Lock to Never. Turn volume on.
                            </span>
                        </li>
                        <li className="flex gap-3">
                            <span className="font-display font-black text-[#FF4F18]">4.</span>
                            <span>Do a test order end-to-end with your own phone. You're done.</span>
                        </li>
                    </ol>
                </div>

                <button
                    data-testid="success-go-admin-btn"
                    onClick={() => nav(`/${slug}/admin`)}
                    className="mt-8 w-full py-5 rounded-full bg-[#FF4F18] text-white font-display font-bold text-lg flex items-center justify-center gap-2 shadow-[0_16px_40px_rgba(255,79,24,0.32)] active:scale-[0.98]"
                >
                    Go to my admin dashboard
                    <ArrowRight className="h-5 w-5" strokeWidth={2.5} />
                </button>
            </div>
        </div>
    );
};

const LinkCard = ({ icon: Icon, title, subtitle, url, onCopy, accent, testid }) => (
    <div className="rounded-3xl bg-white border border-[#E8DCCB] p-5">
        <div className="flex items-start gap-4">
            <div
                className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${accent}18` }}
            >
                <Icon className="h-5 w-5" style={{ color: accent }} strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-display font-black text-base">{title}</div>
                <div className="text-xs text-[#8C7A72] mt-0.5">{subtitle}</div>
                <div
                    className="mt-3 font-mono text-sm bg-[#F9F6F0] border border-[#E8DCCB] rounded-xl px-3 py-2 truncate"
                    data-testid={testid}
                >
                    {url}
                </div>
            </div>
            <button
                data-testid={`${testid}-copy`}
                onClick={onCopy}
                className="shrink-0 h-11 w-11 rounded-2xl bg-[#F9F6F0] border border-[#E8DCCB] flex items-center justify-center active:scale-95"
                title="Copy"
            >
                <Copy className="h-4 w-4" strokeWidth={2.5} />
            </button>
        </div>
    </div>
);

export default SignupSuccess;
